import { memo, useCallback, useMemo } from 'react';
import { Price, cn } from '@divlab/divanui';

import { useMeta } from '@Queries/Meta';
import useNavigation from '@Navigation/useNavigation';
import Link from '@Navigation/Link';
import UILink from '@UI/Link';
import Img from '@UI/Img';
import { useDeps } from '@Contexts/DI';
import styles from './FabricCard.module.css';

import type { ConstructorCollection, ConstructorValueData } from '@Pages/PageProduct/typings';
import type { FC, MouseEvent, PropsWithChildren } from 'react';

interface Props {
  fabric: ConstructorValueData;
  view?: 'grouped';
  className?: string;
  collection?: ConstructorCollection;
  needShowPrice?: boolean;
  tabName: string;
  onClickItem?: (e: MouseEvent, item: ConstructorValueData) => void;
  onClickCollection?: () => void;
}

export interface ContainerProps {
  className?: string;
  item: ConstructorValueData;
  inNewTab: boolean;
  tabName: string;
  onClick?: (e: MouseEvent) => void;
}

const Container: FC<PropsWithChildren<ContainerProps>> = (props) => {
  const { item, inNewTab, tabName, onClick, ...restProps } = props;
  const { analytics } = useDeps();
  const { navigate } = useNavigation();
  const meta = useMeta();
  const { region } = meta.data;

  const handleClickLink = useCallback(() => {
    if (inNewTab) return;

    navigate(`${region.url}${item.link}`, { replace: true });

    analytics.dispatchEvent('constructor.productPage.parameterChange', {
      tabName,
      selectedValue: item.title,
    });
  }, [analytics, inNewTab, item, navigate, region.url, tabName]);

  return item.link ? (
    <Link
      {...restProps}
      to={inNewTab ? item.link : null}
      target={inNewTab ? '_blank' : '_self'}
      onClick={handleClickLink}
      preventDefault={!inNewTab}
    />
  ) : (
    <div {...restProps} onClick={onClick} />
  );
};

const FabricCard = (props: Props) => {
  const {
    fabric,
    needShowPrice = true,
    className,
    collection,
    view,
    tabName,
    onClickItem,
    onClickCollection,
    ...restProps
  } = props;
  const hasDescription = !!collection?.description && collection?.images?.length > 0;

  const handleCollectionClick = useCallback(() => {
    if (!hasDescription) return;

    if (onClickCollection) onClickCollection();
  }, [hasDescription, onClickCollection]);

  const containerParams = useMemo<ContainerProps>(() => {
    return {
      item: fabric,
      inNewTab: collection.id !== 'baseCollection',
      tabName,
      onClick: () => onClickItem(null, fabric),
    };
  }, [fabric, collection.id, tabName, onClickItem]);

  return (
    <div
      {...restProps}
      className={cn(
        styles.card,
        { [styles.selected]: fabric.selected },
        { [styles.hidden]: fabric.hidden },
        { [styles.noDescriptions]: !hasDescription },
        className,
      )}
    >
      <div className={styles.container}>
        <Container {...containerParams}>
          <Img src={fabric.image} className={styles.image} />
        </Container>

        <div className={styles.info}>
          {view !== 'grouped' && collection && (
            <UILink className={styles.collection} view='primary' onClick={handleCollectionClick}>
              {collection.name}
            </UILink>
          )}
          <Container {...containerParams} className={styles.name}>
            {fabric.title}
          </Container>
        </div>

        {needShowPrice && (
          <Container {...containerParams}>
            <Price price={fabric.price} className={styles.price} />
          </Container>
        )}
      </div>
    </div>
  );
};

export default memo(FabricCard);
